//Imports
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select, { components } from "react-select";
import { db, storage } from "../../firebase";
import { doc, updateDoc, Timestamp, arrayUnion, arrayRemove, addDoc, collection } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
//Imports Context
import { AuthContext } from "../../contexts/AuthContext";
import { AppContext } from "../../contexts/AppContext";
import { DepartmentContext } from "../../contexts/DepartmentContext";
import staticData from '../../contexts/staticData.json';
//Imports Components
import ViewPurchaseRequestForm from "../Forms/PurchaseForm/ViewPurchaseRequestForm";
import ViewFeeContractForm from "../Forms/FeeContractForm/ViewFeeContractForm";
import MessageConfirmation from "../messageConfirmation/MessageConfirmation";
import ConfirmationPopup from "./ConfirmationPopup";
//Imports Helpers
import { formatCurrency, formatDate, formatDisplayName, getSubventionNames } from "../../shared/helpers";
//Imports Icons
import downArrow from '../../assets/icons/downArrowWhite.svg';
import upload from '../../assets/icons/upload.svg';
import send from '../../assets/icons/send.svg';
import documentIcon from '../../assets/icons/document.svg';
import historyIcon from '../../assets/icons/history.svg';
import info from '../../assets/icons/info.svg';
import edit from '../../assets/icons/editWhite.svg';
import save from '../../assets/icons/saveWhite.svg';
import warning from '../../assets/icons/info.svg';
//Import CSS
import './viewRequest.css';

function ViewRequest({ request, setSelectedRequest, onRequestResponse, requestObserved, answerObservation, data, departmentName, view, viewObserved, viewResponseObservation, currentRequestType }) {
    // Contexts
    const { userData } = useContext(AuthContext);
    const { establishmentList, pmeList } = useContext(AppContext);
    const { messageConfirmation, showNotificationDepartment, responseRequest, setResponseRequest, supereducAccount, setSupereducAccount } = useContext(DepartmentContext);
    //States
    const [showAdditionalFields, setShowAdditionalFields] = useState(false);
    const [showViewPurchaseRequest, setShowViewPurchaseRequest] = useState(false);
    const [isEditingSupereduc, setIsEditingSupereduc] = useState(false);
    const [isSupereducAccountChanged, setIsSupereducAccountChanged] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
    const [purchaseOrderAmount, setPurchaseOrderAmount] = useState(0);
    const [showPurchaseOrderWarning, setShowPurchaseOrderWarning] = useState(false);
    const [showInsufficientBalanceWarning, setShowInsufficientBalanceWarning] = useState(false);
    const [balanceWarningInfo, setBalanceWarningInfo] = useState({
        requestAmount: 0,
        availableBalance: 0,
        proposedAmount: 0
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const navigate = useNavigate();
    const filteredDepartments = staticData.departmentsSent.filter(dept => dept !== departmentName);

    //Effects
    useEffect(() => {
        const contenedor = document.querySelector('.view-pending-requests'); // o tu clase principal scrollable
        if (contenedor) contenedor.scrollTop = 0;
    }, []);

    useEffect(() => {
        if(request) {
            setSupereducAccount(request.request.requestDetails.supereducAccount);
        }
    }, [request]);


    useEffect(() => {
        if (responseRequest.responseRequest || requestObserved) {
            setShowAdditionalFields(true);
            if (responseRequest.responseRequest?.value === 'rejected') {
                setResponseRequest(prevState => ({
                    ...prevState,
                    sendTo: { value: 'establishment', label: 'Establecimiento' }
                }));
            }
        } else {
            setShowAdditionalFields(false);
        }
    }, [responseRequest.responseRequest, requestObserved]);

    //Helper Functions
    const getEstablishment = () => {
        return establishmentList.find(establishment => establishment.id === request.request?.idEstablishment);
    };

    const isFormValid = () => {
        // Validación común para todos los casos
        const baseValidation = responseRequest.responseRequest &&
            responseRequest.comments.trim() !== '' &&
            (responseRequest.responseRequest.value === 'rejected' || responseRequest.sendTo);

        // Caso base para solicitud observada
        if (requestObserved) {
            return responseRequest.comments.trim() !== '';
        }

        // Validación específica para Departamento Adquisiciones
        if (departmentName === 'Departamento Adquisiciones' && responseRequest.responseRequest?.value === 'approved') {
            const validPurchaseOrderAmount = purchaseOrderAmount > 0;
            return baseValidation && validPurchaseOrderAmount && responseRequest?.backups.length > 0;
        }

        // Para todos los demás casos
        return baseValidation;
    };

    const getObservationDetails = () => {
        if (!request.request.history || request.request.history.length === 0) return null;

        const lastHistoryEntry = request.request.history[request.request.history.length - 1];

        if (lastHistoryEntry.responseRequest === 'observed') {
            return {
                action: lastHistoryEntry.action,
                observedBy: formatDisplayName(lastHistoryEntry.responsibleName),
                role: lastHistoryEntry.role,
                details: lastHistoryEntry.details,
                timestamp: formatDate(lastHistoryEntry.timestamp),
                backups: lastHistoryEntry.backups || [],
                response: lastHistoryEntry.response
            };
        }
        return null;
    };

    //Events Handlers
    const handleChangeSelect = (selectedOption, { name }) => {
        setResponseRequest(prevState => ({
            ...prevState,
            [name]: selectedOption
        }))
    };

    const handleChange = (e) => {
        const { name, files, value } = e.target;
        if (name === 'backups') {
            // Acceder a los archivos seleccionados
            const files = Array.from(e.target.files || []);

            if (files.length === 0) return; // No hacer nada si no hay archivos seleccionados

            // Actualizar el estado con los nuevos archivos
            setResponseRequest(prevState => ({
                ...prevState,
                backups: [...prevState.backups, ...files]
            }));

            // Limpiar el valor del input para permitir seleccionar el mismo archivo
            e.target.value = null;
        } else {
            setResponseRequest(prevState => ({
                ...prevState,
                [name]: value
            }))
        }
    };

    const handleEditSupereduc = () => {
        setIsEditingSupereduc(true);
        setIsSupereducAccountChanged(true);
        showNotificationDepartment(false);
        setErrorMessage(null);
    };

    const handlePurchaseOrderAmountChange = (e) => {
        const value = e.target.value;
        const numericValue = parseInt(value.replace(/[^\d]/g, ''), 10) || 0;
        setPurchaseOrderAmount(numericValue);
    };

    const handleViewFormClick = () => {
        setShowViewPurchaseRequest(true);
    };

    const handleClosePurchaseForm = () => {
        setShowViewPurchaseRequest(false);
    };

    const handleHistoryClick = () => {
        const establishment = getEstablishment()?.nameEstablishment;
        const currentState = {
            previousPath: location.pathname,
            selectedRequest: request,
            data: data,
            viewPendingOpen: true,  // Indica que ViewPending debe permanecer abierto
            requestObserved,
            answerObservation,
            directToViewRequest: true,
            view,
            viewObserved,
            viewResponseObservation,
            currentRequestType
        };

        navigate(`/establecimiento/seguimiento/${request.request.id}`, {
            state: {
                establishment,
                returnState: currentState
            }
        });
    };

    const removeFile = (index) => {
        setResponseRequest(prevState => ({
            ...prevState,
            backups: prevState.backups.filter((_, i) => i !== index)
        }));
    };

    //Firebase Functions
    const uploadFile = async (file) => {
        const storageRef = ref(storage, `requests/${file.name}_${Date.now()}`);
        await uploadBytes(storageRef, file);
        return await getDownloadURL(storageRef);
    };

    const updateSupereducAccount = async () => {
        const requestRef = doc(db, 'requests', request.request.id);
        try {
            await updateDoc(requestRef, {
                'requestDetails.supereducAccount': supereducAccount
            });
            // Actualizar el estado local
            setSelectedRequest(prevState => ({
                ...prevState,
                request: {
                    ...prevState.request,
                    requestDetails: {
                        ...prevState.request.requestDetails,
                        supereducAccount: supereducAccount
                    }
                }
            }));
            // setSupereducAccount()

            showNotificationDepartment(true);
            setIsEditingSupereduc(false);
            setIsSupereducAccountChanged(false);
            setErrorMessage(false);
        } catch (error) {
            console.error('Error al actualizar la cuenta SUPEREDUC:', error);
            setErrorMessage(true);
        }
    };

    const sendResponseToFirestore = async () => {
        if (departmentName !== 'Departamento Adquisiciones' && responseRequest.responseRequest?.value === 'approved' &&
            responseRequest.sendTo?.value === 'Establecimiento' &&
            !request.request.requestDetails.purchaseOrderAmount) {
            setShowPurchaseOrderWarning(true);
            return;
        }
        try {
            setIsSubmitting(true);
            const requestRef = doc(db, 'requests', request.request.id);
            const requestRefPME = doc(db, 'pme', request.request.idPME);
            const getPME = pmeList.find(pme => pme.id === request.request.idPME);
            const getActivityIndex = getPME.activities.findIndex(activity => activity.id === request.request.idActivity);
            const getActivity = getPME.activities[getActivityIndex];
            const oldRequestAmount = request.request.requestDetails.requestAmount;
            const oldPurchaseOrderAmount = request.request.requestDetails.purchaseOrderAmount;

            // Subir archivos
            const uploadedFiles = await Promise.all(
                responseRequest.backups.map(async (file) => {
                    const downloadURL = await uploadFile(file);
                    return { name: file.name, url: downloadURL };
                })
            );

            const historyArray = request.request.history;
            const lastHistoryEntry = historyArray[historyArray.length - 1];
            const penultimateEntry = historyArray[historyArray.length - 2];

            // Crear nueva entrada de historial
            const newHistoryEntry = {
                action: `${requestObserved
                    ? `Observación respondida por ${userData.permissions === 'admin' ? 'Administrador' : userData.department}`
                    : responseRequest.responseRequest?.value === 'rejected'
                        ? `Solicitud rechazada por ${userData.permissions === 'admin' ? 'Administrador' : userData.department}`
                        : responseRequest.responseRequest?.value === 'observed'
                            ? `Solicitud observada por ${userData.permissions === 'admin' ? 'Administrador' : userData.department}`
                            : `Solicitud revisada por ${userData.permissions === 'admin' ? 'Administrador' : userData.department}`
                    }`,
                details: responseRequest.comments,
                responsibleName: userData.displayName,
                role: userData.permissions === 'admin' ? 'Administrador' : userData.department,
                timestamp: Timestamp.now(),
                backups: uploadedFiles,
                ...(requestObserved ? {} : {
                    sentTo: (responseRequest.sendTo?.value === 'establishment') ? 'Establecimiento' : responseRequest.sendTo?.value,
                    responseRequest: responseRequest.responseRequest?.value
                }),
                ...(departmentName === 'Departamento Adquisiciones' && responseRequest.responseRequest?.value === 'approved' && {
                    purchaseOrderAmount: Number(purchaseOrderAmount)
                })
            };

            // Manejar diferentes casos
            if (requestObserved) {
                const updatedHistoryEntry = {
                    ...lastHistoryEntry,
                    response: [newHistoryEntry],
                };

                await updateDoc(requestRef, {
                    history: arrayRemove(lastHistoryEntry)
                });
                await updateDoc(requestRef, {
                    history: arrayUnion(updatedHistoryEntry),
                    'requestDetails.sentTo': penultimateEntry.sentTo
                });

            } else if (responseRequest.responseRequest?.value === 'rejected' ||
                (departmentName === 'Departamento Adquisiciones' && responseRequest.responseRequest?.value === 'approved') ||
                (responseRequest.responseRequest?.value === 'approved' && responseRequest.sendTo?.value === 'Establecimiento')) {

                let updates = {
                    history: arrayUnion(newHistoryEntry)
                };

                // Calcular nuevos saldos si es necesario
                if (responseRequest.responseRequest?.value === 'rejected') {
                    const amountToUSe = oldPurchaseOrderAmount ? oldPurchaseOrderAmount : oldRequestAmount
                    // Actualizar PME
                    const newAccruedExpense = getActivity.accruedExpense - amountToUSe;
                    const newEffectiveExpense = getActivity.effectiveExpense + amountToUSe;

                    updates = {
                        ...updates,
                        'requestDetails.sentTo': 'Establecimiento',
                        'requestDetails.state': 'rejected'
                    };

                    const updatedActivities = getPME.activities.map(act =>
                        act.id === getActivity.id
                            ? { ...act, accruedExpense: newAccruedExpense, effectiveExpense: newEffectiveExpense }
                            : act
                    );
                    await updateDoc(requestRefPME, { activities: updatedActivities });
                    // Crear notificación
                    await addDoc(collection(db, 'notifications'), {
                        requestId: request.request.id,
                        projectId: request.request.idProject,
                        establishmentId: request.request.idEstablishment,
                        requestFolio: request.request.requestDetails.folio,
                        requestType: request.request.typeOfRequest,
                        requestStatus: 'rejected',
                        sentBy: userData.uid,
                        notificationCreated: Timestamp.now(),
                        seenBy: null,
                        viewedAt: null
                    });

                } else if (departmentName === 'Departamento Adquisiciones' && responseRequest.responseRequest?.value === 'approved') {
                    // Calcular el saldo total disponible: saldo devengado + monto de solicitud original
                    const availableBalance = getActivity.effectiveExpense + oldRequestAmount;

                    // Verificar si el monto de OC excede el saldo disponible total
                    if (Number(purchaseOrderAmount) > availableBalance) {
                        setBalanceWarningInfo({
                            requestAmount: oldRequestAmount,
                            effectiveExpense: getActivity.effectiveExpense,
                            availableBalance: availableBalance,
                            proposedAmount: Number(purchaseOrderAmount)
                        });
                        setShowInsufficientBalanceWarning(true);
                        return;
                    }

                    const newAccruedExpense = getActivity.accruedExpense - oldRequestAmount + Number(purchaseOrderAmount);
                    const newEffectiveExpense = getActivity.totalSubsidyAmount - newAccruedExpense - getActivity.accruedBalance;

                    updates = {
                        ...updates,
                        'requestDetails.sentTo': responseRequest.sendTo.value,
                        'requestDetails.purchaseOrderAmount': Number(purchaseOrderAmount),
                        'requestDetails.state': responseRequest.sendTo.value === 'Establecimiento' ? 'approved' : 'pending'
                    };

                    // Actualizar PME
                    const updatedActivities = getPME.activities.map(act =>
                        act.id === getActivity.id
                            ? { ...act, accruedExpense: newAccruedExpense, effectiveExpense: newEffectiveExpense }
                            : act
                    );
                    await updateDoc(requestRefPME, { activities: updatedActivities });

                    // Crear notificación
                    if (responseRequest.sendTo.value === 'Establecimiento') {
                        await addDoc(collection(db, 'notifications'), {
                            requestId: request.request.id,
                            projectId: request.request.idProject,
                            establishmentId: request.request.idEstablishment,
                            requestFolio: request.request.requestDetails.folio,
                            requestType: request.request.typeOfRequest,
                            requestStatus: 'approved',
                            sentBy: userData.uid,
                            notificationCreated: Timestamp.now(),
                            seenBy: null,
                            viewedAt: null
                        });
                    }

                } else if (responseRequest.responseRequest?.value === 'approved' && responseRequest.sendTo?.value === 'Establecimiento') {
                    updates = {
                        ...updates,
                        'requestDetails.sentTo': 'Establecimiento',
                        'requestDetails.state': 'approved'
                    };
                }

                await updateDoc(requestRef, updates);
                await addDoc(collection(db, 'notifications'), {
                    requestId: request.request.id,
                    projectId: request.request.idProject,
                    establishmentId: request.request.idEstablishment,
                    requestFolio: request.request.requestDetails.folio,
                    requestType: request.request.typeOfRequest,
                    requestStatus: 'approved',
                    sentBy: userData.uid,
                    notificationCreated: Timestamp.now(),
                    seenBy: null,
                    viewedAt: null
                });

            } else {
                // Caso por defecto: actualizar solo el historial y destinatario
                await updateDoc(requestRef, {
                    history: arrayUnion(newHistoryEntry),
                    'requestDetails.sentTo': responseRequest.sendTo?.value
                });
            }

            console.log('Solicitud actualizada correctamente');
            setIsSubmitting(false);
            onRequestResponse(true);

        } catch (error) {
            console.error('Error en sendResponseToFirestore:', error);
            onRequestResponse(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    //Main Action Handlers
    const handleSendResponse = async () => {
        if (!isFormValid()) return;


        if (departmentName === 'Departamento SEP' && responseRequest.responseRequest?.value === 'approved' && isSupereducAccountChanged) {
            setShowConfirmationPopup(true);
        } else {
            await sendResponseToFirestore();
        }
    };

    const handleConfirmSendWithChanges = async () => {
        setShowConfirmationPopup(false);
        setIsSupereducAccountChanged(false);
        await updateSupereducAccount();
    };

    const handleCancelSendWithChanges = () => {
        setShowConfirmationPopup(false);
        setIsSupereducAccountChanged(false);
        setSupereducAccount(request.request.requestDetails.supereducAccount);
        setErrorMessage(true);
    };

    const handleCloseWarning = () => {
        setShowPurchaseOrderWarning(false);
        // Resetear el valor de sendTo
        setResponseRequest(prevState => ({
            ...prevState,
            sendTo: null
        }));
    };

    const handleCloseBalanceWarning = () => {
        setShowInsufficientBalanceWarning(false);
        setBalanceWarningInfo({
            requestAmount: 0,
            availableBalance: 0,
            proposedAmount: 0
        })
    };

    const handleBack = () => {
        setSelectedRequest(null);
        setResponseRequest({
            responseRequest: null,
            sendTo: null,
            comments: '',
            backups: []
        });
    };

    // Render Functions
    const renderRequestInfo = () => (
        <article className='info-request flex'>
            <span className='flex'>
                <p>Fecha Solicitud:</p>
                <p>{formatDate(request.request.requestDetails?.created)}</p>
            </span>
            <span className='flex'>
                <p>Establecimiento:</p>
                <p>{getEstablishment().nameEstablishment}</p>
            </span>
            <span className='flex'>
                <p>Responsable Solicitud:</p>
                <p>{formatDisplayName(request.request.requestDetails?.responsibleName)}</p>
            </span>
        </article>
    );

    const renderRequestDetails = () => (
        <article className='container-table flex'>
            <table className='table-details-request'>
                <thead>
                    <tr>
                        <th>Dimensión</th>
                        <th>Nombre Acción</th>
                        <th>Subvención</th>
                        <th>Monto PME</th>
                        <th>Monto Solicitud</th>
                        <th>Gasto Devengado</th>
                        <th>Gasto Efectivo</th>
                        <th>Saldo Devengado</th>
                        <th>Saldo Efectivo</th>
                        <th>Ver Form.</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td data-label='Dimensión'>{formatDisplayName(request.activity?.dimension)}</td>
                        <td data-label='Nombre Acción'>{formatDisplayName(request.activity?.nameOfAction)}</td>
                        <td data-label='Subvención'>{getSubventionNames(request.activity?.subsidies)}</td>
                        <td data-label='Monto PME'>{formatCurrency(request.activity?.totalSubsidyAmount)}</td>
                        <td data-label='Monto Solicitud'>{formatCurrency(request.request?.requestDetails.requestAmount)}</td>
                        <td data-label='Gasto Devengado'>{formatCurrency(request.activity?.accruedExpense)}</td>
                        <td data-label='Gasto Efectivo'>{formatCurrency(request.activity?.accruedBalance)}</td>
                        <td data-label='Saldo Devengado'>{formatCurrency(request.activity?.effectiveExpense)}</td>
                        <td data-label='Saldo Efectivo'>{formatCurrency(request.activity?.effectiveBalance)}</td>
                        <td data-label='Ver Formulario'>
                            <img
                                src={documentIcon}
                                className='icon'
                                alt='Icono para ver formulario'
                                onClick={handleViewFormClick}
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </article>
    );

    const renderObservationDetails = () => {
        const observationDetails = getObservationDetails();
        if (!observationDetails) return null;

        return (
            <article className='observation-details flex'>
                <p className='text-bold'>Observación</p>
                <article className='observation flex'>
                    <header className='observation-header flex'>
                        <img src={info} className='icon' alt='icono de solicitud observada' />
                        <span>{observationDetails.timestamp} {observationDetails.observedBy} ({observationDetails.role}) - <strong>{observationDetails.action}</strong></span>
                    </header>
                    <section className='observation-body flex'>
                        <p><strong>Observación: </strong>{observationDetails.details}</p>
                        {observationDetails.backups.length > 0 && (
                            <span className='observation-backups flex'>
                                <p><strong>Archivos Adjuntos:</strong></p>
                                <ul className='backup-list flex'>
                                    {observationDetails.backups.map((backup, index) => (
                                        <li key={index}>
                                            <a href={backup.url} target='_blank' rel='noopener noreferrer'>{backup.name}</a>
                                        </li>
                                    ))}
                                </ul>
                            </span>
                        )}
                    </section>
                </article>
                {(observationDetails.response && observationDetails.response.length > 0) && (
                    observationDetails.response.map((response, index) => (
                        <article className='observation response flex' key={index}>
                            <header className='observation-header flex'>
                                <img src={info} className='icon' alt='icono de solicitud observada' />
                                <span>{formatDate(response.timestamp)} {formatDisplayName(response.responsibleName)} ({response.role}) - <strong>{response.action}</strong></span>
                            </header>
                            <section className='observation-body flex'>
                                <p><strong>Respuesta: </strong>{response.details}</p>
                                {response.backups.length > 0 && (
                                    <span className='observation-backups flex'>
                                        <p><strong>Archivos Adjuntos:</strong></p>
                                        <ul className='backup-list flex'>
                                            {response.backups.map((backup, index) => (
                                                <li key={index}>
                                                    <a href={backup.url} target='_blank' rel='noopener noreferrer'>{backup.name}</a>
                                                </li>
                                            ))}
                                        </ul>
                                    </span>
                                )}
                            </section>
                        </article>
                    ))
                )}
            </article>
        );
    };

    const renderResponseOptions = () => (
        <article className='response-options flex'>
            <span className='box-select'>
                <label htmlFor='responseRequest' className='label-hidden'>Respuesta de solicitud</label>
                <Select
                    name='responseRequest'
                    id='responseRequest'
                    options={staticData.requestResponses}
                    placeholder='Selecciona Respuesta'
                    className='custom-select'
                    classNamePrefix='custom-select'
                    components={{ DropdownIndicator }}
                    value={responseRequest.responseRequest}
                    onChange={handleChangeSelect}
                />
            </span>
            {(showAdditionalFields && responseRequest.responseRequest?.value !== 'rejected') && (
                <aside className='shipping-information flex'>
                    <p>Enviar a:</p>
                    <span className='box-select'>
                        <label htmlFor='sendTo' className='label-hidden'>Enviar respuesta a:</label>
                        <Select
                            name='sendTo'
                            id='sendTo'
                            options={filteredDepartments
                                // Filtrar Establecimiento si la respuesta es observed
                                .filter(department =>
                                    responseRequest.responseRequest?.value === 'observed'
                                        ? department !== 'Establecimiento'
                                        : true
                                )
                                .map((department) => ({
                                    value: department,
                                    label: department
                                }))}
                            placeholder='Selecciona Departamento'
                            className='custom-select'
                            classNamePrefix='custom-select'
                            components={{ DropdownIndicator }}
                            value={responseRequest.sendTo}
                            onChange={handleChangeSelect}
                        />
                    </span>
                </aside>
            )}
        </article>
    );

    const renderSuperEducAccount = () => (
        <article className={`superEducAccount flex ${showAdditionalFields ? 'show' : ''}`}>
            <p>Cuenta SUPEREDUC:</p>
            <span className='value-superEduc flex'>
                <span className='flex'>
                    <p>Cuenta Contable</p>
                    {isEditingSupereduc ? (
                        <input
                            type='text'
                            className='input'
                            value={supereducAccount}
                            onChange={(e) => {
                                setSupereducAccount(e.target.value)
                                setIsSupereducAccountChanged(true);
                            }}
                            placeholder='Ingresa la nueva cuenta contable...'
                        />
                    ) : (
                        <p className='input'>{supereducAccount || 'No Especificada'}</p>
                    )}
                </span>
                <button
                    className='button-supereduc'
                    type='button'
                    onClick={isEditingSupereduc ? updateSupereducAccount : handleEditSupereduc}
                    title={isEditingSupereduc ? 'Guardar cuenta' : 'Editar cuenta'}
                >
                    <img
                        src={isEditingSupereduc ? save : edit}
                        className='icon'
                        alt='Icono para editar o guardar cuenta supereduc'
                    />
                </button>
            </span>
            {showConfirmationPopup && (
                <ConfirmationPopup
                    message="Hay cambios no guardados en la cuenta SUPEREDUC. ¿Desea guardar estos cambios antes de enviar la respuesta?"
                    onConfirm={handleConfirmSendWithChanges}
                    onCancel={handleCancelSendWithChanges}
                />
            )}
            <MessageConfirmation
                confirmation={messageConfirmation}
                error={errorMessage}
                view={true}
            />
        </article>
    );

    const renderPurchaseOrderDetails = () => (
        <article className={`purchase-order-details flex ${showAdditionalFields ? 'show' : ''}`}>
            <label htmlFor='purchaseOrderAmount'>
                {request.request.typeOfRequest === 'serviceContract'
                    ? 'Ingresar Monto Total Servicio (*):'
                    : request.request.typeOfRequest === 'feeContract'
                        ? 'Ingresar Monto Total Honorarios (*):'
                        : 'Ingresar Monto de Orden de Compra (*):'
                }
            </label>
            <input
                className='input flex'
                type='text'
                id='purchaseOrderAmount'
                value={formatCurrency(purchaseOrderAmount)}
                onChange={handlePurchaseOrderAmountChange}
                placeholder='Ingrese el monto'
            />
        </article>
    );

    const renderCommentsBox = () => (
        <article className={`comments-box flex ${showAdditionalFields ? 'show' : ''}`}>
            <p>{`${requestObserved ? 'Ingresar Respuesta (*)' : 'Ingresar Comentario (*)'}:`}</p>
            <textarea
                name='comments'
                placeholder={`${requestObserved ? 'Ingresa tu respuesta...' : 'Ingresa comentarios...'}`}
                value={responseRequest.comments}
                onChange={handleChange}>
            </textarea>
        </article>
    );

    const renderBackupsBox = () => (
        <article className={`backings-box flex ${showAdditionalFields ? 'show' : ''}`}>
            <p>{departmentName === 'Departamento Adquisiciones'
                ? request.request.typeOfRequest === 'feeContract'
                    ? 'Ajuntar Honorarios (*)'
                    : 'Adjuntar Orden de Compra (*)'
                : 'Adjuntar Respaldos:'}</p>
            <aside className='upload-backups flex'>
                <label className='button-add-backup flex' htmlFor='file-upload'>
                    <img src={upload} className='icon' alt='Icono para subir respaldo.' />
                    <p>Subir Respaldo</p>
                </label>
                <input
                    id='file-upload'
                    name='backups'
                    type='file'
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleChange}
                />
                {(responseRequest.backups.length > 0) && (
                    <ul className='file-list flex'>
                        {responseRequest.backups.map((file, index) => (
                            <li key={index} className='li-item flex' data-filename={file.name} title={file.name}>
                                <p>{file.name}</p>
                                <button type='button' onClick={() => removeFile(index)} aria-label={`Eliminar archivo ${file.name}`} >X</button>
                            </li>
                        ))}
                    </ul>
                )}
            </aside>
        </article>
    );

    const renderActionBox = () => (
        <article className='action-box flex'>
            <button
                type='button'
                className={`send-button flex ${showAdditionalFields ? 'show' : ''} ${isFormValid() ? '' : 'disabled'} ${isSubmitting ? 'disabled' : ''}`}
                disabled={(!isFormValid() && showViewPurchaseRequest) || isSubmitting}
                onClick={handleSendResponse}
            >
                <img
                    src={send}
                    className='icon'
                    alt='Icono para enviar respuesta'
                />
                <p>{isSubmitting ? 'Enviando...' : 'Enviar Respuesta'}</p>
            </button>
            <button type='button' className='back-button flex' onClick={handleBack} disabled={showViewPurchaseRequest}>Volver</button>
        </article>
    );
    //Main Render
    return (
        <section className={`view-request flex ${showViewPurchaseRequest ? 'view-purchase' : ''} `}>
            <header className='header-view-request flex'>
                <p className='text-bold'>Detalles de Solicitud</p>
                <button type='button' className='request-history-button flex' onClick={handleHistoryClick}>
                    <img src={historyIcon} className='icon' alt='Icono para ver historial de solicitud' />
                    <p>Ver Historial Solicitud</p>
                </button>
            </header>
            {renderRequestInfo()}
            {renderRequestDetails()}
            {(answerObservation || requestObserved) && renderObservationDetails()}
            <article className='request-response-box flex'>
                <p className='text-bold'>{`${requestObserved ? 'Respuesta a Observación' : 'Respuesta Solicitud'}`}</p>
                {!requestObserved && renderResponseOptions()}
                {(departmentName === 'Departamento SEP' && responseRequest.responseRequest?.value === 'approved') && renderSuperEducAccount()}
                {(departmentName === 'Departamento Adquisiciones' && responseRequest.responseRequest?.value === 'approved') && renderPurchaseOrderDetails()}
                {renderCommentsBox()}
                {renderBackupsBox()}
                <span className={`warning-popup-overlay flex ${showPurchaseOrderWarning ? 'show' : ''}`}>
                    <aside className={`warning-popup flex ${showPurchaseOrderWarning ? 'show' : ''}`}>
                        <img src={warning} className='icon' alt='Icono de advertencia' />
                        <p>La solicitud no tiene monto de Orden de Compra ingresado. <br></br>Debe ser enviada al <strong>Departamento de Adquisiciones</strong> para su ingreso.</p>
                        <button className='button flex' type='button' onClick={handleCloseWarning}><p>Aceptar</p></button>
                    </aside>
                </span>
                <span className={`warning-popup-overlay flex ${showInsufficientBalanceWarning ? 'show' : ''}`}>
                    <aside className={`warning-popup flex ${showInsufficientBalanceWarning ? 'show' : ''}`}>
                        <img src={warning} className='icon' alt='Icono de advertencia' />
                        <p>El monto máximo permitido para la Orden de Compra es <strong>{formatCurrency(balanceWarningInfo.availableBalance)}</strong>. <br></br>Por favor, ingrese un monto que no exceda este valor.</p>
                        <button className='button flex' type='button' onClick={handleCloseBalanceWarning}><p>Aceptar</p></button>
                    </aside>
                </span>
            </article>
            {renderActionBox()}
            {(showViewPurchaseRequest && request) && (
                request.request?.typeOfRequest === 'feeContract' ? (
                    <ViewFeeContractForm
                        onClose={handleClosePurchaseForm}
                        requestDetails={request}
                        establishmentList={establishmentList}
                        fromViewRequest={true}
                    />
                ) : (
                    <ViewPurchaseRequestForm
                        onClose={handleClosePurchaseForm}
                        requestDetails={request}
                        establishmentList={establishmentList}
                        fromViewRequest={true}
                    />
                )
            )}
        </section>
    );
};

const DropdownIndicator = (props) => {
    return (
        <components.DropdownIndicator {...props}>
            <img src={downArrow} alt="CustomArrow" />
        </components.DropdownIndicator>
    );
};

export default ViewRequest;